import { useState } from "react";

import type { ButtonProps } from "@mui/material";
import { Button, Menu, MenuItem } from "@mui/material";

type MenuOption<T extends string = string> = {
  dataTestId?: string;
  disabled?: boolean;
  label: string;
  value: T;
};

interface Props<T extends string = string> {
  buttonProps?: ButtonProps;
  options: MenuOption<T>[];
  handleMenuItemClick: (option: MenuOption<T>) => void;
  renderButton?: (onClick: (event: React.MouseEvent<HTMLElement>) => void) => React.ReactNode;
}

export const MUIPositionedMenu = <T extends string = string>({
  buttonProps,
  options = [],
  handleMenuItemClick,
  renderButton,
}: Props<T>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      {renderButton ? (
        renderButton(handleClick)
      ) : (
        <Button
          id="positioned-button"
          aria-controls={open ? "positioned-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
          {...buttonProps}
        />
      )}
      <Menu
        id="positioned-menu"
        aria-labelledby="positioned-button"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {options.map(option => (
          <MenuItem
            key={option.value}
            data-testid={option.dataTestId}
            disabled={option.disabled}
            onClick={() => {
              handleClose();
              handleMenuItemClick(option);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};
